import React, {FC, PropsWithChildren, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Renderer} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {SettingsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {ProgressBar} from '../../../../../libs/shared/ui/src/components/progressBar';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';

/*
 * Props.
 */

export interface SetupGuideProps extends PropsWithChildren {
  title: ReactNode;
  iconName: IconName;
  settingsIconName?: IconName;
  progress: number;
  onDismiss?: () => void;
  helpCenterUrl?: string;
  renderFooter?: Renderer;
  settingsPanelId?: SettingsPanelId;
  className?: string;
}

/*
 * Style.
 */

const StyledSetupGuideDiv = styled.div`
  border-radius: 16px;
  background: ${(p) => p.theme.foreground};
  ${(p) => boxShadow(p.theme, `0 1px 9px ${p.theme.alphas.black20}`)};
  border: 1px solid ${(p) => p.theme.alphas.black20};
  padding: 19px 19px 10px 19px;

  display: flex;
  flex-direction: column;
  align-content: center;
`;

const StyledHeaderDiv = styled.div`
  ${flexCenter}
  flex-direction: row;
  margin-bottom: 16px;
`;

const StyledIcon = styled(Icon)`
  margin-right: 11px;
`;

const StyledTitleSpan = styled.span`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 16px;
`;

const StyledPercentageDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.bold};
  line-height: 16px;
  margin-left: auto;
`;

/** Negative margins as the dismiss icon is nested into the padding of the container. */
const StyledDismissDiv = styled.div`
  margin-top: -10px;
  margin-right: -10px;
  margin-left: auto;
`;

const StyledTasksDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledFooterDiv = styled.div`
  margin-top: 9px;
`;

const StyledLearnMoreButton = styled(Button)`
  display: inline-block;
  margin-right: auto;
  margin-left: -10px;
`;

const StyledLearnMoreDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
`;

const StyledLearnMoreIcon = styled(Icon)`
  margin-left: 4px;
`;

const StyledSettingsIcon = styled(Icon)`
  flex-basis: 20px;
  margin: 10px;
`;

const StyledSettingsHeaderDiv = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 18px;
`;

const StyledSettingsIconDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  border-radius: 10px;
  background: ${(p) => p.theme.palette.blue.shade20};
  color: ${(p) => p.theme.interactiveText};
  display: block;
  height: 40px;
  width: 40px;

  margin-right: 16px;
`;

const StyledSettingsTitleSpan = styled.span`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 16px;
  margin-top: 12px;
`;

const StyledSettingsFooterDiv = styled.div`
  margin-top: auto;
  margin-bottom: 9px;
`;

const StyledSettingsTasksDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 9px;
`;

/*
 * Component.
 */

export const SetupGuide: FC<SetupGuideProps> = (props) => {
  const {
    children,
    title,
    iconName,
    settingsIconName,
    progress,
    onDismiss,
    helpCenterUrl,
    renderFooter,
    settingsPanelId,
    className,
  } = props;
  if (settingsPanelId) {
    return (
      <StyledSetupGuideDiv className={className}>
        <StyledSettingsHeaderDiv>
          <StyledSettingsIconDiv>
            {settingsIconName && <StyledSettingsIcon name={settingsIconName} />}
          </StyledSettingsIconDiv>
          <StyledSettingsTitleSpan>{title}</StyledSettingsTitleSpan>
          {renderProgressOrDismiss(progress, onDismiss)}
        </StyledSettingsHeaderDiv>

        <ProgressBar progress={progress} />

        <StyledSettingsTasksDiv>{children}</StyledSettingsTasksDiv>

        <StyledSettingsFooterDiv>{renderFooter?.()}</StyledSettingsFooterDiv>
      </StyledSetupGuideDiv>
    );
  }

  return (
    <StyledSetupGuideDiv className={className}>
      <StyledHeaderDiv>
        <StyledIcon name={iconName} />
        <StyledTitleSpan>{title}</StyledTitleSpan>
        {renderProgressOrDismiss(progress, onDismiss)}
      </StyledHeaderDiv>

      <ProgressBar progress={progress} />

      <StyledTasksDiv>{children}</StyledTasksDiv>

      {helpCenterUrl && <StyledFooterDiv>{renderLearnMore(helpCenterUrl)}</StyledFooterDiv>}
    </StyledSetupGuideDiv>
  );
};

/*
 * Helpers.
 */

function renderProgressOrDismiss(progress: number, onDismiss?: () => void) {
  if (progress >= 100) {
    return (
      <StyledDismissDiv>
        <IconButton iconName="closeSmall" label="Dismiss" onClick={onDismiss} />
      </StyledDismissDiv>
    );
  }

  return <StyledPercentageDiv>{progress}%</StyledPercentageDiv>;
}

function renderLearnMore(helpCenterUrl: string) {
  return (
    <StyledLearnMoreButton
      onClick={() => openInBrowser(helpCenterUrl)}
      size={VisualSizesEnum.MEDIUM}
      variant={ButtonVariantsEnum.LINK}
    >
      <StyledLearnMoreDiv>
        <Trans id="accounts-setup-guide-learn-more">Learn more</Trans>
        <StyledLearnMoreIcon name="externalLinkSmall" />
      </StyledLearnMoreDiv>
    </StyledLearnMoreButton>
  );
}
